<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { Logs, BadgePlus, ListRestart, Trash2 } from "lucide-svelte"
  import { xhr, toast, formatDate } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  const plan: PlanType = appStore.plan

  let { id, type }: { id: number; type: string } = $props()

  let activity_logs: ActivityLogType[] = $state([])

  let loading = $state(true)

  onMount(async () => {
    getActivityLogs()
  })

  let getActivityLogs = async function () {
    try {
      const resp = await xhr.get(
        `/plans/${plan.id}/activity_logs?id=${id}&type=${type}`
      )

      activity_logs = resp.data
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the history", "error")
    } finally {
      activity_logs = activity_logs
      loading = false
    }
  }

  function statusName(status) {
    if (status === "on_track") {
      return "<span class='text-on_track font-bold'>On Track</span>"
    } else if (status === "at_risk") {
      return "<span class='text-at_risk font-bold'>At Risk</span>"
    } else if (status === "critical") {
      return "<span class='text-critical font-bold'>Critical</span>"
    } else if (status === "complete") {
      return "<span class='text-complete font-bold'>Complete</span>"
    } else if (status === "past_due") {
      return "<span class='text-past_due font-bold'>Past Due</span>"
    } else if (status === "blocked") {
      return "<span class='text-blocked font-bold'>Blocked</span>"
    } else if (status === "on_hold") {
      return "<span class='text-on_hold font-bold'>On Hold</span>"
    } else if (status === "not_met") {
      return "<span class='text-not_met font-bold'>Not Met</span>"
    } else if (status === "unknown") {
      return "<span class='text-gray-700 font-bold'>Unknown</span>"
    }
  }

  function visibilityName(visibility) {
    if (visibility === "plan") {
      return "All Members"
    } else if (visibility === "leadership") {
      return "Only Leadership"
    }
  }

  function updateMessage(log) {
    let data = JSON.parse(log.data)
    if (log.update_detail === "name") {
      return `The name was updated from <span class='text-gray-800 italic'>${data[0]}</span> to <span class='text-gray-700 font-bold'>${data[1]}</span>`
    } else if (log.update_detail === "status") {
      return `The status was updated from ${statusName(data[0])} to ${statusName(data[1])}`
    } else if (log.update_detail === "assignee") {
      return `The assignee was updated from ${data[0]} to ${data[1]}`
    } else if (log.update_detail === "expected_at" && data[0] !== null) {
      return `The expected date was updated from ${data[0]} to ${data[1]}`
    } else if (log.update_detail === "expected_at" && data[0] === null) {
      return `The expected date was set to ${data[1]}`
    } else if (log.update_detail === "completed_at" && data[0] !== null) {
      return `The completed date was updated from ${data[0]} to ${data[1]}`
    } else if (log.update_detail === "completed_at" && data[0] === null) {
      return `The completed date was set to <span class="text-gray-700 font-bold">${data[1]}</span>`
    } else if (log.update_detail === "description") {
      return `The description was updated to the following: <div class="mt-2 mx-4 border-l border-dashed border-gray-400 p-2">${data[1]}</div>`
    } else if (log.update_detail === "action_plan") {
      return `The action plan was updated to the following: <div class="mt-2 mx-4 border-l border-dashed border-gray-400 p-2">${data[1]}</div>`
    } else if (log.update_detail === "obstacles_text") {
      return `The obstacles was updated to the following: <div class="mt-2 mx-4 border-l border-dashed border-gray-400 p-2">${data[1]}</div>`
    } else if (log.update_detail === "visibility") {
      return `The visibility date was updated from <span class="text-gray-800 italic">${visibilityName(data[0])}</span> to  <span class="font-bold">${visibilityName(data[1])}</span>`
    }
  }
</script>

<h3 class="flex items-center mb-1">
  <Logs class="text-gray-400 size-4 mr-1" />
  History
</h3>
<div class="overflow-y-auto" data-name="progresses">
  {#if loading}
    <div class="py-12 flex justify-center w-full">
      <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
    </div>
  {:else if activity_logs.length > 0}
    <div class="flow-root mt-6">
      <ul role="list" class="-mb-8">
        {#each activity_logs as log}
          <li>
            <div class="relative pb-8">
              <span
                class="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                aria-hidden="true"
              ></span>
              <div class="relative flex space-x-3">
                <div>
                  <span
                    class="flex size-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white shadow"
                  >
                    {#if log.activity === "created"}
                      <BadgePlus class="text-green-400 size-6" />
                    {:else if log.activity === "updated"}
                      <ListRestart class="text-blue-400 size-6" />
                    {:else if log.activity === "deleted"}
                      <Trash2 class="text-red-400 size-6" />
                    {/if}
                  </span>
                </div>
                <div
                  class="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5"
                >
                  <div>
                    <p class="text-sm text-gray-500 flex items-center">
                      <span
                        class="whitespace-nowrap text-xs text-yellow-800 mr-2 bg-yellow-50 p-0.5 rounded-md border border-yellow-100"
                      >
                        <time datetime="2020-09-20"
                          >{formatDate(log.created_at)}</time
                        >
                      </span>
                      {#if log.activity === "created"}
                        {log.subject_type} was created by
                        <span class="text-gray-800 ml-1">{log.who}</span>
                      {:else if log.activity === "updated"}
                        {log.subject_type} was updated by
                        <span class="text-gray-800 ml-1">{log.who}</span>
                      {:else if log.activity === "deleted"}
                        {log.subject_type} was deleted by
                        <span class="text-gray-800 ml-1">{log.who}</span>
                      {/if}
                    </p>
                    {#if log.activity === "updated"}
                      <div
                        class="border-l p-2 border-gray-300 mt-2 shadow-sm text-sm text-gray-600"
                      >
                        {@html updateMessage(log)}
                      </div>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</div>
